import React, { useState, useEffect, useRef } from "react";

const G = "#3A9D23";
const G_LIGHT = "#f0faf0";

const SECTIONS = [
  { id: "responsable", title: "1. Responsable du traitement" },
  { id: "donnees", title: "2. Données collectées" },
  { id: "finalites", title: "3. Finalités du traitement" },
  { id: "base-legale", title: "4. Base légale" },
  { id: "tiers", title: "5. Partage avec des tiers" },
  { id: "duree", title: "6. Durée de conservation" },
  { id: "droits", title: "7. Vos droits" },
  { id: "cookies", title: "8. Cookies" },
  { id: "modifications", title: "9. Modifications" },
];

function SectionBlock({ id, title, children }) {
  return (
    <section id={id} className="mb-12 scroll-mt-24">
      <h2
        className="text-xl font-semibold mb-4 pb-3 border-b"
        style={{ color: "#1a1a1a", borderColor: "#e5e7eb" }}
      >
        {title}
      </h2>
      <div className="text-gray-600 leading-relaxed space-y-3 text-[15px]">
        {children}
      </div>
    </section>
  );
}

function Tag({ children }) {
  return (
    <span
      className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mr-1 mb-1"
      style={{ backgroundColor: G_LIGHT, color: G }}
    >
      {children}
    </span>
  );
}

function InfoBox({ children }) {
  return (
    <div
      className="rounded-xl p-4 text-sm mt-3"
      style={{ backgroundColor: G_LIGHT, borderLeft: `3px solid ${G}` }}
    >
      {children}
    </div>
  );
}

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState("responsable");
  const observerRef = useRef(null);

  // Highlight active section in sidebar as user scrolls
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* Header */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <a href="/" className="text-gray-400 hover:text-gray-600 text-sm transition-colors">
            Komorotel
          </a>
          <span className="text-gray-300">/</span>
          <span className="text-sm text-gray-700 font-medium">Politique de confidentialité</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">

        {/* Page title */}
        <div className="mb-12">
          <h1
            className="text-3xl md:text-4xl font-bold mb-3"
            style={{ fontFamily: "'Playfair Display', serif", color: "#1a1a1a" }}
          >
            Politique de confidentialité
          </h1>
          <p className="text-gray-400 text-sm">
            Dernière mise à jour : Juin 2026 · Version 1.0
          </p>
          <p className="text-gray-500 mt-3 max-w-2xl leading-relaxed">
            Komorotel s'engage à protéger vos données personnelles. Cette page explique
            quelles données nous collectons, pourquoi, et quels sont vos droits.
          </p>
        </div>

        <div className="flex gap-12">

          {/* Sidebar — sticky, hidden on mobile */}
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Sommaire
              </p>
              <nav className="space-y-1">
                {SECTIONS.map(({ id, title }) => {
                  const isActive = activeSection === id;
                  return (
                    <button
                      key={id}
                      onClick={() => scrollTo(id)}
                      className="block w-full text-left text-sm px-3 py-2 rounded-lg transition-all duration-150"
                      style={{
                        color: isActive ? G : "#6b7280",
                        backgroundColor: isActive ? G_LIGHT : "transparent",
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {title}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">

            <SectionBlock id="responsable" title="1. Responsable du traitement">
              <p>
                Le responsable du traitement des données collectées sur la plateforme Komorotel est :
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mt-2">
                <p className="font-medium text-gray-800">Komorotel</p>
                <p className="text-sm mt-1">Moroni, Grande Comore, Comores</p>
                <p className="text-sm">
                  Email :{" "}
                  <a href="mailto:contact@komorotel.com" style={{ color: G }}>
                    contact@komorotel.com
                  </a>
                </p>
                <p className="text-sm">
                  Site :{" "}
                  <a href="https://komorotel.com" style={{ color: G }}>
                    www.komorotel.com
                  </a>
                </p>
              </div>
            </SectionBlock>

            <SectionBlock id="donnees" title="2. Données collectées">
              <p>Nous collectons uniquement les données nécessaires au fonctionnement de la plateforme :</p>

              <div className="mt-2">
                <p className="font-medium text-gray-700 mb-1">Données d'identification</p>
                <div>
                  <Tag>Nom et prénom</Tag>
                  <Tag>Adresse email</Tag>
                  <Tag>Numéro de téléphone</Tag>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Collectées lors de la création de votre compte voyageur ou hébergeur.
                </p>
              </div>

              <div className="mt-4">
                <p className="font-medium text-gray-700 mb-1">Données de réservation</p>
                <div>
                  <Tag>Dates de séjour</Tag>
                  <Tag>Hébergement choisi</Tag>
                  <Tag>Nombre de voyageurs</Tag>
                  <Tag>Montant de la réservation</Tag>
                  <Tag>Historique des séjours</Tag>
                </div>
              </div>

              <div className="mt-4">
                <p className="font-medium text-gray-700 mb-1">Données techniques</p>
                <div>
                  <Tag>Adresse IP</Tag>
                  <Tag>Type de navigateur</Tag>
                  <Tag>Pages consultées</Tag>
                  <Tag>Durée de visite</Tag>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Collectées automatiquement via Google Analytics (uniquement avec votre consentement).
                </p>
              </div>

              <InfoBox>
                Nous ne collectons pas de données de paiement — les paiements se font directement
                entre le voyageur et l'hébergeur. Aucune carte bancaire n'est stockée sur nos serveurs.
              </InfoBox>
            </SectionBlock>

            <SectionBlock id="finalites" title="3. Finalités du traitement">
              <p>Vos données sont utilisées exclusivement pour :</p>
              <ul className="space-y-2 mt-2">
                {[
                  "Créer et gérer votre compte utilisateur",
                  "Traiter et confirmer vos réservations",
                  "Envoyer les emails transactionnels (confirmation, annulation, rappel)",
                  "Permettre aux hébergeurs partenaires de préparer votre séjour",
                  "Améliorer la plateforme via des analyses de trafic anonymisées",
                  "Répondre à vos demandes de support",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span
                      className="mt-1 shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-white text-xs"
                      style={{ backgroundColor: G }}
                    >
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </SectionBlock>

            <SectionBlock id="base-legale" title="4. Base légale du traitement">
              <p>Selon le type de données, le traitement repose sur :</p>
              <div className="space-y-3 mt-2">
                <div className="flex gap-3">
                  <span
                    className="shrink-0 text-xs font-semibold px-2 py-1 rounded-md h-fit"
                    style={{ backgroundColor: G_LIGHT, color: G }}
                  >
                    Contrat
                  </span>
                  <p>
                    Données d'identification et de réservation — nécessaires à l'exécution du
                    service de réservation que vous avez demandé.
                  </p>
                </div>
                <div className="flex gap-3">
                  <span
                    className="shrink-0 text-xs font-semibold px-2 py-1 rounded-md h-fit"
                    style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
                  >
                    Consentement
                  </span>
                  <p>
                    Cookies analytiques Google Analytics — uniquement si vous avez accepté
                    via notre bannière de consentement. Vous pouvez retirer ce consentement
                    à tout moment.
                  </p>
                </div>
                <div className="flex gap-3">
                  <span
                    className="shrink-0 text-xs font-semibold px-2 py-1 rounded-md h-fit"
                    style={{ backgroundColor: "#ede9fe", color: "#5b21b6" }}
                  >
                    Intérêt légitime
                  </span>
                  <p>
                    Sécurité de la plateforme, prévention des fraudes et amélioration
                    de l'expérience utilisateur.
                  </p>
                </div>
              </div>
            </SectionBlock>

            <SectionBlock id="tiers" title="5. Partage avec des tiers">
              <p>
                Nous ne vendons jamais vos données. Elles peuvent être partagées uniquement avec :
              </p>
              <div className="space-y-3 mt-3">
                {[
                  {
                    name: "Hébergeurs partenaires",
                    desc: "Nom, prénom et dates de séjour transmis à l'hébergeur concerné par votre réservation uniquement.",
                  },
                  {
                    name: "Google Analytics",
                    desc: "Données de navigation anonymisées pour analyser le trafic. Activé uniquement avec votre consentement.",
                  },
                  {
                    name: "Hostinger",
                    desc: "Notre hébergeur VPS. Accès serveur dans le cadre de la fourniture de l'infrastructure technique.",
                  },
                ].map((t, i) => (
                  <div key={i} className="rounded-xl border border-gray-100 p-4">
                    <p className="font-medium text-gray-800 text-sm">{t.name}</p>
                    <p className="text-sm text-gray-500 mt-1">{t.desc}</p>
                  </div>
                ))}
              </div>
            </SectionBlock>

            <SectionBlock id="duree" title="6. Durée de conservation">
              <div className="space-y-3">
                {[
                  { label: "Compte actif", value: "Données conservées tant que votre compte est actif." },
                  { label: "Après suppression du compte", value: "3 ans maximum (obligations légales et comptables)." },
                  { label: "Logs serveur", value: "1 an, conformément aux obligations légales." },
                  { label: "Données de réservation", value: "10 ans à compter de la réservation (obligation comptable)." },
                ].map((row, i) => (
                  <div key={i} className="flex gap-4 py-3 border-b border-gray-50 last:border-0">
                    <span className="font-medium text-gray-700 text-sm w-48 shrink-0">{row.label}</span>
                    <span className="text-gray-500 text-sm">{row.value}</span>
                  </div>
                ))}
              </div>
            </SectionBlock>

            <SectionBlock id="droits" title="7. Vos droits">
              <p>Conformément au RGPD, vous disposez des droits suivants sur vos données :</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {[
                  { title: "Droit d'accès", desc: "Obtenir une copie de vos données." },
                  { title: "Droit de rectification", desc: "Corriger des données inexactes." },
                  { title: "Droit à l'effacement", desc: "Demander la suppression de vos données." },
                  { title: "Droit d'opposition", desc: "Vous opposer à certains traitements." },
                  { title: "Droit à la portabilité", desc: "Récupérer vos données dans un format lisible." },
                  { title: "Retrait du consentement", desc: "Désactiver les cookies analytiques à tout moment." },
                ].map((r, i) => (
                  <div key={i} className="rounded-xl bg-gray-50 p-3">
                    <p className="font-medium text-gray-800 text-sm">{r.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{r.desc}</p>
                  </div>
                ))}
              </div>
              <InfoBox>
                Pour exercer vos droits, contactez-nous à{" "}
                <a href="mailto:contact@komorotel.com" style={{ color: G }} className="font-medium">
                  contact@komorotel.com
                </a>
                . Nous répondons sous 30 jours maximum.
              </InfoBox>
            </SectionBlock>

            <SectionBlock id="cookies" title="8. Cookies">
              <p>Nous utilisons deux types de cookies :</p>
              <div className="space-y-3 mt-3">
                <div className="rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-gray-800 text-sm">Cookies essentiels</p>
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: G_LIGHT, color: G }}
                    >
                      Toujours actifs
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Authentification, sécurité, préférences. Indispensables au fonctionnement
                    du site. Ne nécessitent pas de consentement.
                  </p>
                </div>
                <div className="rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-gray-800 text-sm">Cookies analytiques</p>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      Sur consentement
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Google Analytics — mesure d'audience avec données anonymisées. Activés
                    uniquement si vous avez accepté via la bannière cookies.
                  </p>
                </div>
              </div>
              <p className="mt-3 text-sm">
                Vous pouvez modifier vos préférences à tout moment en{" "}
                <button
                  onClick={() => {
                    localStorage.removeItem("komorotel_cookie_consent");
                    window.location.reload();
                  }}
                  className="underline font-medium transition-colors"
                  style={{ color: G }}
                >
                  réinitialisant vos préférences cookies
                </button>
                .
              </p>
            </SectionBlock>

            <SectionBlock id="modifications" title="9. Modifications">
              <p>
                Nous pouvons mettre à jour cette politique à tout moment. En cas de modification
                substantielle, vous serez informé par email ou via une notification sur la plateforme
                au moins 15 jours avant l'entrée en vigueur.
              </p>
              <p>
                La date de dernière mise à jour est indiquée en haut de cette page. La poursuite
                de l'utilisation de Komorotel après modification vaut acceptation de la nouvelle
                politique.
              </p>
              <p>
                Pour toute question :{" "}
                <a href="mailto:contact@komorotel.com" style={{ color: G }} className="font-medium">
                  contact@komorotel.com
                </a>
              </p>
            </SectionBlock>

          </main>
        </div>
      </div>
    </div>
  );
}